import type { SchedulingDefinition, SchedulingPlan } from "./types";

export const planStatuses: Record<SchedulingPlan["status"], string> = {
  active: "Scheduled", paused: "Paused", completed: "Schedule ended",
  cancelled: "Cancelled", uncertain: "Needs checking", failed: "Failed",
};
export const planZone = (plan: SchedulingPlan) => plan.definition.timing.type === "calendar"
  ? plan.definition.timing.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
export function planTime(at: number | null, zone?: string): string {
  if (at == null) return "None";
  return new Date(at).toLocaleString(undefined, { timeZone: zone, weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
export function planCadence(plan: SchedulingPlan): string {
  const t = plan.definition.timing;
  if (t.type === "once") return "Once";
  if (t.type === "dates") return `${t.entries.length} scheduled steps`;
  if (t.type === "interval") {
    const unit = t.everyMs % 86400000 === 0 ? [86400000, "day"] as const : t.everyMs % 3600000 === 0 ? [3600000, "hour"] as const : [60000, "minute"] as const;
    const n = t.everyMs / unit[0]; return `Repeats every ${n} ${unit[1]}${n === 1 ? "" : "s"}`;
  }
  const period = t.frequency === "daily" ? "day" : t.frequency === "weekly" ? "week" : "month";
  const days = t.frequency === "weekly" ? ` · ${(t.weekdays ?? []).map(n => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][n - 1]).join(", ")}` : t.frequency === "monthly" ? ` · days ${(t.monthDays ?? []).join(", ")}` : "";
  return `Repeats every ${t.every} ${period}${t.every === 1 ? "" : "s"}${days} · ${t.times.join(", ")}`;
}
export function planDelivery(plan: SchedulingPlan, now = Date.now()): string {
  if (plan.status !== "active") return planStatuses[plan.status];
  if (plan.deliveryState === "uncertain") return "Delivery needs checking";
  if (plan.deliveryState === "queued" || plan.deliveryState === "pending") return "Queued for this chat";
  if (plan.deliveryState === "preparing") return "Preparing delivery";
  if (plan.deliveryState === "sending") return "Delivering prompt";
  return plan.nextDueAt != null && plan.nextDueAt <= now ? "Waiting to run" : "Scheduled";
}
export function planGap(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000)), d = Math.floor(s / 86400), h = Math.floor(s % 86400 / 3600), m = Math.floor(s % 3600 / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : m ? `${m}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
}
/** Dots count accepted deliveries, never completed business work. Large and
 * unbounded schedules never allocate an unbounded DOM or invent a total. */
export function planProgress(plan: SchedulingPlan) {
  const def = plan.definition, t = def.timing;
  const finite = t.type === "once" ? 1 : t.type === "dates"
    ? t.entries.filter(e => def.endAt == null || Date.parse(e.at) <= Date.parse(def.endAt)).length : null;
  const total = finite == null ? def.maxRuns : Math.min(finite, def.maxRuns ?? finite);
  const sent = Math.max(0, plan.runsAccepted), shown = Math.min(12, total ?? sent);
  return { total, shown, sent, label: total == null ? `${sent} sent · no run limit` : finite == null ? `${sent} sent · up to ${total}` : `${sent} of ${total} sent`, overflow: total != null && total > shown };
}
export const runLabel = (state: string) => ({ accepted: "Sent to chat", pending: "Waiting", preparing: "Preparing delivery", sending: "Delivery in progress", uncertain: "Needs checking", failed: "Failed", skipped: "Skipped", cancelled: "Cancelled" }[state] ?? state);

/** Edit only selected fields on a deep copy; preserve end/misfire/interrupt,
 * alerts, anchor seconds and unchanged dated entries exactly. */
export const copyDefinition = (definition: SchedulingDefinition): SchedulingDefinition => structuredClone(definition);
export function localDateTime(iso: string): string {
  const d = new Date(iso), pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
export function editedInstant(value: string): string {
  const at = new Date(value);
  if (!value || !Number.isFinite(at.getTime())) throw new Error("Choose a valid date and time.");
  // Reject browser normalization of a nonexistent local DST wall time.
  if (localDateTime(at.toISOString()).slice(0, value.length) !== value) throw new Error("That local time does not exist. Choose a different time.");
  return at.toISOString();
}
